/**
 * Admin Users Page
 * Manage admin users (SUPER_ADMIN only)
 */

import { useCallback, useEffect, useState } from "react"
import { useAuth } from "../context/AuthContext"
import { apiClient } from "../lib/api"
import { formatDate } from "../lib/utils"
import type { AdminUser } from "../types"

interface CreateAdminUserForm {
  username: string
  email: string
  password: string
  role: "super_admin" | "admin" | "viewer"
}

export function AdminUsersPage() {
  const { user } = useAuth()
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isCreating, setIsCreating] = useState(false)
  const [formData, setFormData] = useState<CreateAdminUserForm>({
    username: "",
    email: "",
    password: "",
    role: "super_admin",
  })
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof CreateAdminUserForm | "root", string>>>({})

  // Edit state
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null)
  const [isUpdating, setIsUpdating] = useState(false)

  // Delete state
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [deletingUser, setDeletingUser] = useState<AdminUser | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Reset password state
  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false)
  const [resettingUser, setResettingUser] = useState<AdminUser | null>(null)
  const [isResettingPassword, setIsResettingPassword] = useState(false)
  const [newPassword, setNewPassword] = useState("")
  const [resetPasswordError, setResetPasswordError] = useState<string>("")

  const validateForm = (): boolean => {
    const errors: Partial<Record<keyof CreateAdminUserForm | "root", string>> = {}

    if (!formData.username || formData.username.length < 3) {
      errors.username = "Username must be at least 3 characters"
    }
    if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Invalid email address"
    }
    if (!formData.password || formData.password.length < 8) {
      errors.password = "Password must be at least 8 characters"
    }
    if (!formData.role) {
      errors.role = "Please select a role"
    }

    setFormErrors(errors)
    return Object.keys(errors).length === 0
  }

  const fetchAdminUsers = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)
      const response = await apiClient.listAdminUsers({
        page: 1,
        limit: 100,
        sortBy: "createdAt",
        sortOrder: "desc",
      })
      setAdminUsers(response.data)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch admin users")
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAdminUsers()
  }, [fetchAdminUsers])

  const handleCreateAdminUser = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    try {
      setIsCreating(true)
      setFormErrors({})
      await apiClient.createAdminUser(formData)
      setIsCreateDialogOpen(false)
      setFormData({ username: "", email: "", password: "", role: "super_admin" })
      await fetchAdminUsers()
    } catch (err) {
      setFormErrors({
        root: err instanceof Error ? err.message : "Failed to create admin user",
      })
    } finally {
      setIsCreating(false)
    }
  }

  const handleEditUser = (adminUser: AdminUser) => {
    setEditingUser(adminUser)
    setIsEditDialogOpen(true)
  }

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editingUser) return

    try {
      setIsUpdating(true)
      await apiClient.updateAdminUser(editingUser.id, {
        role: editingUser.role,
        status: editingUser.status as "active" | "inactive",
      })
      setIsEditDialogOpen(false)
      setEditingUser(null)
      await fetchAdminUsers()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update admin user")
    } finally {
      setIsUpdating(false)
    }
  }

  const handleDeleteUser = (adminUser: AdminUser) => {
    setDeletingUser(adminUser)
    setIsDeleteDialogOpen(true)
  }

  const confirmDelete = async () => {
    if (!deletingUser) return

    try {
      setIsDeleting(true)
      await apiClient.deleteAdminUser(deletingUser.id)
      setIsDeleteDialogOpen(false)
      setDeletingUser(null)
      await fetchAdminUsers()
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete admin user")
    } finally {
      setIsDeleting(false)
    }
  }

  const handleResetPassword = (adminUser: AdminUser) => {
    setResettingUser(adminUser)
    setNewPassword("")
    setResetPasswordError("")
    setIsResetPasswordOpen(true)
  }

  const confirmResetPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!resettingUser) return

    if (!newPassword || newPassword.length < 8) {
      setResetPasswordError("Password must be at least 8 characters")
      return
    }

    try {
      setIsResettingPassword(true)
      setResetPasswordError("")
      await apiClient.resetAdminPassword(resettingUser.id, newPassword)
      setIsResetPasswordOpen(false)
      setResettingUser(null)
      setNewPassword("")
      alert(`Password reset successfully for ${resettingUser.username}`)
    } catch (err) {
      setResetPasswordError(err instanceof Error ? err.message : "Failed to reset password")
    } finally {
      setIsResettingPassword(false)
    }
  }

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case "super_admin":
        return "destructive"
      case "admin":
        return "primary"
      case "viewer":
        return "secondary"
      default:
        return "default"
    }
  }

  // Check if current user is SUPER_ADMIN
  const isSuperAdmin = user?.role === "super_admin"

  if (!isSuperAdmin) {
    return (
      <div style={{ padding: "2rem" }}>
        <h1 style={{ fontSize: "1.875rem", fontWeight: 700, color: "#f1f5f9", marginBottom: "1.5rem" }}>
          Admin Users
        </h1>
        <div
          style={{
            padding: "1.5rem",
            background: "rgba(239, 68, 68, 0.1)",
            border: "1px solid rgba(239, 68, 68, 0.3)",
            borderRadius: "12px",
          }}
        >
          <h3 style={{ fontSize: "1.125rem", fontWeight: 600, color: "#fca5a5", marginBottom: "0.5rem" }}>
            Access Denied
          </h3>
          <p style={{ color: "#f87171", fontSize: "0.9375rem" }}>
            You do not have permission to view this page. Only SUPER_ADMIN users can manage admin users.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div style={{ padding: "2rem" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "2rem" }}>
        <div>
          <h1 style={{ fontSize: "1.875rem", fontWeight: 700, color: "#f1f5f9", marginBottom: "0.5rem" }}>
            Admin Users
          </h1>
          <p style={{ color: "#94a3b8", fontSize: "0.9375rem" }}>Manage admin users and their permissions</p>
        </div>
        <button
          type="button"
          onClick={() => setIsCreateDialogOpen(true)}
          style={{
            padding: "0.75rem 1.5rem",
            background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
            border: "none",
            borderRadius: "12px",
            color: "white",
            fontSize: "0.9375rem",
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.3s ease",
            boxShadow: "0 0 20px rgba(139, 92, 246, 0.3)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)"
            e.currentTarget.style.boxShadow = "0 0 30px rgba(139, 92, 246, 0.4)"
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)"
            e.currentTarget.style.boxShadow = "0 0 20px rgba(139, 92, 246, 0.3)"
          }}
        >
          Add Admin User
        </button>
      </div>

      {error && (
        <div
          style={{
            padding: "1rem",
            background: "rgba(239, 68, 68, 0.1)",
            border: "1px solid rgba(239, 68, 68, 0.3)",
            borderRadius: "12px",
            marginBottom: "1.5rem",
          }}
        >
          <p style={{ color: "#f87171", fontSize: "0.9375rem" }}>{error}</p>
        </div>
      )}

      <div className="card">
        {isLoading ? (
          <div style={{ textAlign: "center", padding: "3rem" }}>
            <p style={{ color: "#94a3b8", fontSize: "0.9375rem" }}>Loading admin users...</p>
          </div>
        ) : adminUsers.length === 0 ? (
          <div style={{ textAlign: "center", padding: "3rem" }}>
            <p style={{ color: "#94a3b8", fontSize: "0.9375rem" }}>No admin users found</p>
          </div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Last Login</th>
                  <th>Created At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {adminUsers.map((adminUser) => (
                  <tr key={adminUser.id}>
                    <td>
                      <span style={{ fontWeight: 600, color: "#e2e8f0" }}>{adminUser.username}</span>
                    </td>
                    <td>{adminUser.email}</td>
                    <td>
                      <span className={`badge badge-${getRoleBadgeColor(adminUser.role)}`}>
                        {adminUser.role.replace("_", " ").toUpperCase()}
                      </span>
                    </td>
                    <td>
                      <span className={`badge badge-${adminUser.status === "active" ? "success" : "secondary"}`}>
                        {adminUser.status.toUpperCase()}
                      </span>
                    </td>
                    <td style={{ fontSize: "0.875rem", color: "#94a3b8" }}>
                      {adminUser.lastLogin ? formatDate(adminUser.lastLogin) : "Never"}
                    </td>
                    <td style={{ fontSize: "0.875rem", color: "#94a3b8" }}>
                      {formatDate(adminUser.createdAt)}
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: "0.5rem" }}>
                        <button
                          type="button"
                          onClick={() => handleEditUser(adminUser)}
                          disabled={user?.id === adminUser.id}
                          style={{
                            padding: "0.375rem 0.75rem",
                            background: user?.id === adminUser.id ? "rgba(100, 116, 139, 0.3)" : "rgba(99, 102, 241, 0.2)",
                            border: "1px solid rgba(99, 102, 241, 0.3)",
                            borderRadius: "6px",
                            color: user?.id === adminUser.id ? "#64748b" : "#a78bfa",
                            fontSize: "0.8125rem",
                            fontWeight: 500,
                            cursor: user?.id === adminUser.id ? "not-allowed" : "pointer",
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleResetPassword(adminUser)}
                          disabled={user?.id === adminUser.id}
                          style={{
                            padding: "0.375rem 0.75rem",
                            background: user?.id === adminUser.id ? "rgba(100, 116, 139, 0.3)" : "rgba(34, 197, 94, 0.2)",
                            border: "1px solid rgba(34, 197, 94, 0.3)",
                            borderRadius: "6px",
                            color: user?.id === adminUser.id ? "#64748b" : "#4ade80",
                            fontSize: "0.8125rem",
                            fontWeight: 500,
                            cursor: user?.id === adminUser.id ? "not-allowed" : "pointer",
                          }}
                        >
                          Reset Password
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteUser(adminUser)}
                          disabled={user?.id === adminUser.id}
                          style={{
                            padding: "0.375rem 0.75rem",
                            background: user?.id === adminUser.id ? "rgba(100, 116, 139, 0.3)" : "rgba(239, 68, 68, 0.2)",
                            border: "1px solid rgba(239, 68, 68, 0.3)",
                            borderRadius: "6px",
                            color: user?.id === adminUser.id ? "#64748b" : "#f87171",
                            fontSize: "0.8125rem",
                            fontWeight: 500,
                            cursor: user?.id === adminUser.id ? "not-allowed" : "pointer",
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Admin User Modal */}
      {isCreateDialogOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.7)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50,
          }}
          onClick={() => {
            setIsCreateDialogOpen(false)
            setFormErrors({})
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setIsCreateDialogOpen(false)
              setFormErrors({})
            }
          }}
        >
          <div
            className="card"
            style={{ maxWidth: "500px", width: "90%", maxHeight: "90vh", overflow: "auto" }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontSize: "1.5rem", fontWeight: 700, color: "#f1f5f9", marginBottom: "0.5rem" }}>
              Add Admin User
            </h2>
            <p style={{ color: "#94a3b8", fontSize: "0.875rem", marginBottom: "1.5rem" }}>
              Create a new admin user. Only SUPER_ADMIN users can create other admin users.
            </p>

            <form onSubmit={handleCreateAdminUser}>
              <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                <div>
                  <label htmlFor="username" style={{ display: "block", marginBottom: "0.5rem", fontSize: "0.875rem", color: "#e2e8f0" }}>
                    Username
                  </label>
                  <input
                    id="username"
                    type="text"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder="john_doe"
                    style={{
                      width: "100%",
                      padding: "0.625rem 1rem",
                      background: "rgba(255, 255, 255, 0.05)",
                      border: `1px solid ${formErrors.username ? "rgba(239, 68, 68, 0.5)" : "rgba(255, 255, 255, 0.1)"}`,
                      borderRadius: "8px",
                      color: "#f1f5f9",
                      fontSize: "0.9375rem",
                    }}
                  />
                  {formErrors.username && (
                    <p style={{ color: "#f87171", fontSize: "0.75rem", marginTop: "0.25rem" }}>{formErrors.username}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="email" style={{ display: "block", marginBottom: "0.5rem", fontSize: "0.875rem", color: "#e2e8f0" }}>
                    Email
                  </label>
                  <input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@example.com"
                    style={{
                      width: "100%",
                      padding: "0.625rem 1rem",
                      background: "rgba(255, 255, 255, 0.05)",
                      border: `1px solid ${formErrors.email ? "rgba(239, 68, 68, 0.5)" : "rgba(255, 255, 255, 0.1)"}`,
                      borderRadius: "8px",
                      color: "#f1f5f9",
                      fontSize: "0.9375rem",
                    }}
                  />
                  {formErrors.email && (
                    <p style={{ color: "#f87171", fontSize: "0.75rem", marginTop: "0.25rem" }}>{formErrors.email}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="password" style={{ display: "block", marginBottom: "0.5rem", fontSize: "0.875rem", color: "#e2e8f0" }}>
                    Password
                  </label>
                  <input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Minimum 8 characters"
                    style={{
                      width: "100%",
                      padding: "0.625rem 1rem",
                      background: "rgba(255, 255, 255, 0.05)",
                      border: `1px solid ${formErrors.password ? "rgba(239, 68, 68, 0.5)" : "rgba(255, 255, 255, 0.1)"}`,
                      borderRadius: "8px",
                      color: "#f1f5f9",
                      fontSize: "0.9375rem",
                    }}
                  />
                  {formErrors.password && (
                    <p style={{ color: "#f87171", fontSize: "0.75rem", marginTop: "0.25rem" }}>{formErrors.password}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="role" style={{ display: "block", marginBottom: "0.5rem", fontSize: "0.875rem", color: "#e2e8f0" }}>
                    Role
                  </label>
                  <select
                    id="role"
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value as "super_admin" | "admin" | "viewer" })}
                    style={{
                      width: "100%",
                      padding: "0.625rem 1rem",
                      background: "rgba(255, 255, 255, 0.05)",
                      border: `1px solid ${formErrors.role ? "rgba(239, 68, 68, 0.5)" : "rgba(255, 255, 255, 0.1)"}`,
                      borderRadius: "8px",
                      color: "#f1f5f9",
                      fontSize: "0.9375rem",
                    }}
                  >
                    <option value="super_admin">Super Admin - Full access including user management</option>
                    <option value="admin">Admin - Can view and write</option>
                    <option value="viewer">Viewer - Can only view</option>
                  </select>
                  {formErrors.role && (
                    <p style={{ color: "#f87171", fontSize: "0.75rem", marginTop: "0.25rem" }}>{formErrors.role}</p>
                  )}
                </div>

                {formErrors.root && (
                  <div
                    style={{
                      padding: "0.75rem",
                      background: "rgba(239, 68, 68, 0.1)",
                      border: "1px solid rgba(239, 68, 68, 0.3)",
                      borderRadius: "8px",
                    }}
                  >
                    <p style={{ color: "#f87171", fontSize: "0.875rem" }}>{formErrors.root}</p>
                  </div>
                )}
              </div>

              <div style={{ display: "flex", gap: "0.75rem", marginTop: "1.5rem", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={() => {
                    setIsCreateDialogOpen(false)
                    setFormErrors({})
                    setFormData({ username: "", email: "", password: "", role: "super_admin" })
                  }}
                  disabled={isCreating}
                  style={{
                    padding: "0.625rem 1.25rem",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "8px",
                    color: "#e2e8f0",
                    fontSize: "0.9375rem",
                    fontWeight: 500,
                    cursor: isCreating ? "not-allowed" : "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isCreating}
                  style={{
                    padding: "0.625rem 1.25rem",
                    background: isCreating
                      ? "rgba(99, 102, 241, 0.5)"
                      : "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                    border: "none",
                    borderRadius: "8px",
                    color: "white",
                    fontSize: "0.9375rem",
                    fontWeight: 600,
                    cursor: isCreating ? "not-allowed" : "pointer",
                    transition: "all 0.3s ease",
                  }}
                >
                  {isCreating ? "Creating..." : "Create Admin User"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Admin User Modal */}
      {isEditDialogOpen && editingUser && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.7)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50,
          }}
          onClick={() => {
            setIsEditDialogOpen(false)
            setEditingUser(null)
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setIsEditDialogOpen(false)
              setEditingUser(null)
            }
          }}
        >
          <div
            className="card"
            style={{ maxWidth: "500px", width: "90%", maxHeight: "90vh", overflow: "auto" }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontSize: "1.5rem", fontWeight: 700, color: "#f1f5f9", marginBottom: "0.5rem" }}>
              Edit Admin User
            </h2>
            <p style={{ color: "#94a3b8", fontSize: "0.875rem", marginBottom: "1.5rem" }}>
              Update the role and status of {editingUser.username}
            </p>

            <form onSubmit={handleUpdateUser}>
              <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                <div>
                  <label htmlFor="edit-username" style={{ display: "block", marginBottom: "0.5rem", fontSize: "0.875rem", color: "#e2e8f0" }}>
                    Username
                  </label>
                  <input
                    id="edit-username"
                    type="text"
                    value={editingUser.username}
                    disabled
                    style={{
                      width: "100%",
                      padding: "0.625rem 1rem",
                      background: "rgba(100, 116, 139, 0.2)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      borderRadius: "8px",
                      color: "#94a3b8",
                      fontSize: "0.9375rem",
                      cursor: "not-allowed",
                    }}
                  />
                </div>

                <div>
                  <label htmlFor="edit-email" style={{ display: "block", marginBottom: "0.5rem", fontSize: "0.875rem", color: "#e2e8f0" }}>
                    Email
                  </label>
                  <input
                    id="edit-email"
                    type="email"
                    value={editingUser.email}
                    disabled
                    style={{
                      width: "100%",
                      padding: "0.625rem 1rem",
                      background: "rgba(100, 116, 139, 0.2)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      borderRadius: "8px",
                      color: "#94a3b8",
                      fontSize: "0.9375rem",
                      cursor: "not-allowed",
                    }}
                  />
                </div>

                <div>
                  <label htmlFor="edit-role" style={{ display: "block", marginBottom: "0.5rem", fontSize: "0.875rem", color: "#e2e8f0" }}>
                    Role
                  </label>
                  <select
                    id="edit-role"
                    value={editingUser.role}
                    onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value as "super_admin" | "admin" | "viewer" })}
                    style={{
                      width: "100%",
                      padding: "0.625rem 1rem",
                      background: "rgba(255, 255, 255, 0.05)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      borderRadius: "8px",
                      color: "#f1f5f9",
                      fontSize: "0.9375rem",
                    }}
                  >
                    <option value="super_admin">Super Admin - Full access including user management</option>
                    <option value="admin">Admin - Can view and write</option>
                    <option value="viewer">Viewer - Can only view</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="edit-status" style={{ display: "block", marginBottom: "0.5rem", fontSize: "0.875rem", color: "#e2e8f0" }}>
                    Status
                  </label>
                  <select
                    id="edit-status"
                    value={editingUser.status}
                    onChange={(e) => setEditingUser({ ...editingUser, status: e.target.value })}
                    style={{
                      width: "100%",
                      padding: "0.625rem 1rem",
                      background: "rgba(255, 255, 255, 0.05)",
                      border: "1px solid rgba(255, 255, 255, 0.1)",
                      borderRadius: "8px",
                      color: "#f1f5f9",
                      fontSize: "0.9375rem",
                    }}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div style={{ display: "flex", gap: "0.75rem", marginTop: "1.5rem", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditDialogOpen(false)
                    setEditingUser(null)
                  }}
                  disabled={isUpdating}
                  style={{
                    padding: "0.625rem 1.25rem",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "8px",
                    color: "#e2e8f0",
                    fontSize: "0.9375rem",
                    fontWeight: 500,
                    cursor: isUpdating ? "not-allowed" : "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isUpdating}
                  style={{
                    padding: "0.625rem 1.25rem",
                    background: isUpdating
                      ? "rgba(99, 102, 241, 0.5)"
                      : "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
                    border: "none",
                    borderRadius: "8px",
                    color: "white",
                    fontSize: "0.9375rem",
                    fontWeight: 600,
                    cursor: isUpdating ? "not-allowed" : "pointer",
                    transition: "all 0.3s ease",
                  }}
                >
                  {isUpdating ? "Updating..." : "Update Admin User"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteDialogOpen && deletingUser && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.7)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50,
          }}
          onClick={() => {
            setIsDeleteDialogOpen(false)
            setDeletingUser(null)
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setIsDeleteDialogOpen(false)
              setDeletingUser(null)
            }
          }}
        >
          <div
            className="card"
            style={{ maxWidth: "450px", width: "90%" }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontSize: "1.5rem", fontWeight: 700, color: "#f1f5f9", marginBottom: "0.5rem" }}>
              Delete Admin User
            </h2>
            <p style={{ color: "#94a3b8", fontSize: "0.9375rem", marginBottom: "1.5rem" }}>
              Are you sure you want to delete <strong style={{ color: "#e2e8f0" }}>{deletingUser.username}</strong>? This action cannot be undone.
            </p>

            <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
              <button
                type="button"
                onClick={() => {
                  setIsDeleteDialogOpen(false)
                  setDeletingUser(null)
                }}
                disabled={isDeleting}
                style={{
                  padding: "0.625rem 1.25rem",
                  background: "rgba(255, 255, 255, 0.05)",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  borderRadius: "8px",
                  color: "#e2e8f0",
                  fontSize: "0.9375rem",
                  fontWeight: 500,
                  cursor: isDeleting ? "not-allowed" : "pointer",
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={isDeleting}
                style={{
                  padding: "0.625rem 1.25rem",
                  background: isDeleting
                    ? "rgba(239, 68, 68, 0.5)"
                    : "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
                  border: "none",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "0.9375rem",
                  fontWeight: 600,
                  cursor: isDeleting ? "not-allowed" : "pointer",
                  transition: "all 0.3s ease",
                }}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {isResetPasswordOpen && resettingUser && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0, 0, 0, 0.7)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 50,
          }}
          onClick={() => {
            setIsResetPasswordOpen(false)
            setResettingUser(null)
            setNewPassword("")
            setResetPasswordError("")
          }}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              setIsResetPasswordOpen(false)
              setResettingUser(null)
              setNewPassword("")
              setResetPasswordError("")
            }
          }}
        >
          <div
            className="card"
            style={{ maxWidth: "450px", width: "90%" }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontSize: "1.5rem", fontWeight: 700, color: "#f1f5f9", marginBottom: "0.5rem" }}>
              Reset Admin Password
            </h2>
            <p style={{ color: "#94a3b8", fontSize: "0.9375rem", marginBottom: "1.5rem" }}>
              Reset password for <strong style={{ color: "#e2e8f0" }}>{resettingUser.username}</strong>. The new password must be at least 8 characters long.
            </p>

            <form onSubmit={confirmResetPassword}>
              <div style={{ marginBottom: "1.5rem" }}>
                <label htmlFor="reset-password" style={{ display: "block", marginBottom: "0.5rem", fontSize: "0.875rem", color: "#e2e8f0" }}>
                  New Password
                </label>
                <input
                  id="reset-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                  style={{
                    width: "100%",
                    padding: "0.625rem 1rem",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: `1px solid ${resetPasswordError ? "rgba(239, 68, 68, 0.5)" : "rgba(255, 255, 255, 0.1)"}`,
                    borderRadius: "8px",
                    color: "#f1f5f9",
                    fontSize: "0.9375rem",
                  }}
                />
                {resetPasswordError && (
                  <p style={{ color: "#f87171", fontSize: "0.75rem", marginTop: "0.25rem" }}>{resetPasswordError}</p>
                )}
              </div>

              <div style={{ display: "flex", gap: "0.75rem", justifyContent: "flex-end" }}>
                <button
                  type="button"
                  onClick={() => {
                    setIsResetPasswordOpen(false)
                    setResettingUser(null)
                    setNewPassword("")
                    setResetPasswordError("")
                  }}
                  disabled={isResettingPassword}
                  style={{
                    padding: "0.625rem 1.25rem",
                    background: "rgba(255, 255, 255, 0.05)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    borderRadius: "8px",
                    color: "#e2e8f0",
                    fontSize: "0.9375rem",
                    fontWeight: 500,
                    cursor: isResettingPassword ? "not-allowed" : "pointer",
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isResettingPassword}
                  style={{
                    padding: "0.625rem 1.25rem",
                    background: isResettingPassword
                      ? "rgba(34, 197, 94, 0.5)"
                      : "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
                    border: "none",
                    borderRadius: "8px",
                    color: "white",
                    fontSize: "0.9375rem",
                    fontWeight: 600,
                    cursor: isResettingPassword ? "not-allowed" : "pointer",
                    transition: "all 0.3s ease",
                  }}
                >
                  {isResettingPassword ? "Resetting..." : "Reset Password"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
